/**
 * Borrower Assignment Modal (Sheet)
 *
 * Simplified 2-step flow for assigning conditions to the borrower:
 * 1. Select conditions (with optional notes)
 * 2. Success confirmation
 *
 * Unlike TitleEscrowAssignModal, there is no recipient selection,
 * no document attachment step, and no portal token generation.
 * Submitting with 0 selections clears all borrower assignments.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  User,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { EncompassCondition } from '@/types/approval-letter';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';

const log = createComponentLogger('BorrowerAssignModal', 'conditions-portal');

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface BorrowerAssignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conditions: EncompassCondition[];
  loanId: string;
  existingAssignments: Set<string>;
  existingNotes: Record<string, string>;
  onSuccess: () => void;
}

interface ConditionSelection {
  note: string;
}

type Step = 'select' | 'success' | 'error';

/**
 * Get the canonical ID for a condition (externalId preferred)
 */
const getConditionId = (c: EncompassCondition): string | undefined =>
  c.externalId || c.id || c.internalId;

export const BorrowerAssignModal = ({
  open,
  onOpenChange,
  conditions,
  loanId,
  existingAssignments,
  existingNotes,
  onSuccess
}: BorrowerAssignModalProps) => {
  const [step, setStep] = useState<Step>('select');
  const [selections, setSelections] = useState<Map<string, ConditionSelection>>(new Map());
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultCount, setResultCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Non-removed conditions only
  const activeConditions = useMemo(
    () => conditions.filter(c => !c.isRemoved),
    [conditions]
  );

  // Filter by search
  const filteredConditions = useMemo(() => {
    if (!searchQuery.trim()) {
return activeConditions;
}
    const q = searchQuery.toLowerCase();
    return activeConditions.filter(c =>
      (c.title && c.title.toLowerCase().includes(q)) ||
      (c.externalDescription && c.externalDescription.toLowerCase().includes(q)) ||
      (c.internalDescription && c.internalDescription.toLowerCase().includes(q)) ||
      (c.category && c.category.toLowerCase().includes(q))
    );
  }, [activeConditions, searchQuery]);

  // Refs keep parent-prop values reachable at seed time without being effect dependencies.
  // Parent prop churn (polling, refetches) must not reset in-progress edits while the
  // modal is open. A `userEditedRef` gate lets late-arriving initial assignments seed
  // the modal (preload race) while still protecting user edits.
  const existingAssignmentsRef = useRef(existingAssignments);
  const existingNotesRef = useRef(existingNotes);
  const activeConditionsRef = useRef(activeConditions);
  existingAssignmentsRef.current = existingAssignments;
  existingNotesRef.current = existingNotes;
  activeConditionsRef.current = activeConditions;

  const userEditedRef = useRef(false);

  const seedSelectionsFromRefs = () => {
    const initial = new Map<string, ConditionSelection>();
    for (const c of activeConditionsRef.current) {
      const id = getConditionId(c);
      if (id && existingAssignmentsRef.current.has(id)) {
        initial.set(id, { note: existingNotesRef.current[id] || '' });
      }
    }
    setSelections(initial);
    setOpenNotes(new Set());
  };

  // Seed on open / loan change. Always resets the user-edited gate.
  useEffect(() => {
    if (open) {
      userEditedRef.current = false;
      setStep('select');
      setErrorMessage('');
      setSearchQuery('');
      seedSelectionsFromRefs();
    }
     
  }, [open, loanId]);

  // Preload-race fix: if the user opens the modal before the parent's
  // fetchAllAssignments resolves, existingAssignments arrives as a changed
  // prop afterward. Re-seed to pick it up — but only if the user hasn't
  // touched anything yet, so live in-progress edits are preserved.
  useEffect(() => {
    if (!open) {
return;
}
    if (userEditedRef.current) {
return;
}
    seedSelectionsFromRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAssignments, existingNotes, activeConditions]);

  const toggleCondition = (conditionId: string) => {
    userEditedRef.current = true;
    setSelections(prev => {
      const next = new Map(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.set(conditionId, { note: '' });
      }
      return next;
    });
  };

  const toggleNoteOpen = (conditionId: string) => {
    setOpenNotes(prev => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
next.delete(conditionId);
} else {
next.add(conditionId);
}
      return next;
    });
  };

  const updateNote = (conditionId: string, note: string) => {
    userEditedRef.current = true;
    setSelections(prev => {
      const next = new Map(prev);
      if (next.has(conditionId)) {
        next.set(conditionId, { note: note.substring(0, 250) });
      }
      return next;
    });
  };

  const toggleAll = () => {
    userEditedRef.current = true;
    if (selections.size === activeConditions.length) {
      setSelections(new Map());
      setOpenNotes(new Set());
    } else {
      const newSelections = new Map<string, ConditionSelection>();
      activeConditions.forEach(c => {
        const id = getConditionId(c);
        if (id) {
          const existingNote = selections.get(id)?.note || '';
          newSelections.set(id, { note: existingNote });
        }
      });
      setSelections(newSelections);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const conditionsWithNotes = Array.from(selections.entries()).map(([conditionId, data]) => ({
        conditionId,
        note: data.note.trim() || undefined
      }));

      log.info('Submitting borrower assignment', {
        count: conditionsWithNotes.length,
        withNotes: conditionsWithNotes.filter(c => c.note).length
      });

      const { data: sessionData } = await supabase.auth.getSession();
      const freshJwt = sessionData.session?.access_token;
      if (!freshJwt) {
throw new Error('Session expired. Please refresh the page.');
}

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${freshJwt}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          action: 'assign-borrower-internal',
          loanId,
          conditions: conditionsWithNotes
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to assign conditions');
      }

      log.info('Borrower conditions assigned', { assignedCount: data.assignedCount });
      setResultCount(data.assignedCount);
      setStep('success');
      onSuccess();
    } catch (error) {
      log.error('Borrower assignment failed', { error });
      setErrorMessage(error instanceof Error ? error.message : 'Failed to assign conditions');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('select');
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="!w-[60vw] !max-w-none sm:!max-w-none p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-[#183C2E]" />
            <SheetTitle>
              {step === 'select' && 'Assign Conditions to Borrower'}
              {step === 'success' && 'Assignment Complete'}
              {step === 'error' && 'Error'}
            </SheetTitle>
          </div>
          <SheetDescription>
            {step === 'select' && 'Select conditions the borrower needs to address. Only assigned conditions will appear in the borrower portal.'}
            {step === 'success' && 'Conditions have been assigned successfully.'}
            {step === 'error' && 'There was a problem assigning conditions.'}
          </SheetDescription>
        </SheetHeader>

        {/* SELECT STEP */}
        {step === 'select' && (
          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              <div className="px-6 py-4 space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search conditions..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>

                {/* Select header */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    Select Conditions ({selections.size} of {activeConditions.length})
                  </label>
                  <Button variant="ghost" size="sm" onClick={toggleAll} className="text-xs h-7">
                    {selections.size === activeConditions.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {/* Condition list */}
                {filteredConditions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {searchQuery ? 'No conditions match your search.' : 'No conditions available.'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {filteredConditions.map(condition => {
                      const conditionId = getConditionId(condition);
                      const isSelected = conditionId ? selections.has(conditionId) : false;
                      const currentNote = conditionId ? selections.get(conditionId)?.note || '' : '';
                      const description = condition.externalDescription || condition.internalDescription;

                      return (
                        <div
                          key={conditionId}
                          className={cn(
                            'p-4 rounded-lg transition-colors',
                            isSelected
                              ? 'bg-[#183C2E]/5 border-2 border-[#183C2E]/30'
                              : 'hover:bg-muted border border-border'
                          )}
                        >
                          <div
                            className="flex items-start gap-3 cursor-pointer"
                            onClick={() => conditionId && toggleCondition(conditionId)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => conditionId && toggleCondition(conditionId)}
                              onClick={e => e.stopPropagation()}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium leading-tight">
                                {condition.title || 'Untitled Condition'}
                              </p>
                              {description && (
                                <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                  {description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 pt-2">
                                <span className={cn(
                                  'text-xs font-medium px-2 py-0.5 rounded',
                                  condition.priorTo === 'Docs' ? 'bg-blue-100 text-blue-700'
                                    : condition.priorTo === 'Funding' ? 'bg-amber-100 text-amber-700'
                                      : 'bg-purple-100 text-purple-700'
                                )}>
                                  {condition.priorTo === 'Docs' ? 'Prior to Docs'
                                    : condition.priorTo === 'Funding' ? 'Prior to Funding'
                                      : 'At Closing'}
                                </span>
                                {condition.category && (
                                  <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                    {condition.category}
                                  </span>
                                )}
                                {!condition.statusOpen && (
                                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                                    Cleared
                                  </Badge>
                                )}
                              </div>

                              {/* Collapsible note */}
                              {isSelected && conditionId && (
                                <Collapsible
                                  open={openNotes.has(conditionId)}
                                  onOpenChange={() => toggleNoteOpen(conditionId)}
                                >
                                  <div className="border-t border-border/50 mt-3 pt-2">
                                    <CollapsibleTrigger
                                      className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <span className="flex items-center gap-1.5">
                                        <ChevronDown className={cn(
                                          'h-4 w-4 transition-transform duration-200',
                                          openNotes.has(conditionId) && 'rotate-180'
                                        )} />
                                        {currentNote ? 'Edit Note' : 'Add Note'}
                                      </span>
                                      {currentNote && (
                                        <span className="text-xs">{currentNote.length}/250</span>
                                      )}
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                                      <Textarea
                                        placeholder="Add instructions for the borrower..."
                                        value={currentNote}
                                        onChange={e => updateNote(conditionId, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        className="mt-2 min-h-[60px] max-h-[100px] text-sm resize-none"
                                        maxLength={250}
                                      />
                                      <div className="text-xs text-muted-foreground text-right mt-1">
                                        {currentNote.length}/250
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : selections.size === 0 ? (
                  'Clear All Assignments'
                ) : (
                  `Assign ${selections.size} Condition${selections.size !== 1 ? 's' : ''} to Borrower`
                )}
              </Button>
            </div>
          </div>
        )}

        {/* SUCCESS STEP */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-6 py-4 space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-medium text-green-800">
                    {resultCount > 0
                      ? 'Borrower Conditions Assigned'
                      : 'Borrower Assignments Cleared'}
                  </p>
                  <p className="text-sm text-green-700">
                    {resultCount > 0
                      ? `${resultCount} condition${resultCount !== 1 ? 's' : ''} assigned to the borrower.`
                      : 'All borrower assignments have been cleared.'}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Use "View Portal" → "View Borrower Portal" to preview what the borrower will see.
              </p>
            </div>
            <div className="mt-auto px-6 py-4 border-t bg-background">
              <Button onClick={handleClose} className="w-full">Done</Button>
            </div>
          </div>
        )}

        {/* ERROR STEP */}
        {step === 'error' && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-6 py-4">
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Failed to Assign Conditions</p>
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            </div>
            <div className="mt-auto px-6 py-4 border-t bg-background flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">Cancel</Button>
              <Button onClick={() => setStep('select')} className="flex-1">Try Again</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default BorrowerAssignModal;
